import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { X, Monitor } from "lucide-react";

import styles from "./team.module.scss";
import Spinner from "../../components/spinner";

const API = `${process.env.REACT_APP_API_ROOT}/api/admin`;

const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: `Bearer ${user.token}` };
};

/** Trims a user-agent string down to something readable in the table. */
const describeDevice = (ua = "") => {
  if (!ua) return "Unknown device";
  const browser =
    /Edg\//.test(ua) ? "Edge"
      : /Chrome\//.test(ua) ? "Chrome"
      : /Safari\//.test(ua) ? "Safari"
      : /Firefox\//.test(ua) ? "Firefox"
      : "Browser";
  const os =
    /Windows/.test(ua) ? "Windows"
      : /Mac OS X/.test(ua) ? "macOS"
      : /Android/.test(ua) ? "Android"
      : /iPhone|iPad/.test(ua) ? "iOS"
      : /Linux/.test(ua) ? "Linux"
      : "";
  return os ? `${browser} on ${os}` : browser;
};

/** Devices one admin is signed in on, each individually revocable. */
function SessionsModal({ admin, onClose, onRevokedAll }) {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admins/${admin._id}/sessions`, {
        headers: authHeaders(),
      });
      setSessions(data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.error || "Could not load sessions");
    } finally {
      setLoading(false);
    }
  }, [admin._id]);

  useEffect(() => {
    load();
  }, [load]);

  const revokeOne = async (sessionId) => {
    try {
      await axios.delete(`${API}/admins/${admin._id}/sessions/${sessionId}`, {
        headers: authHeaders(),
      });
      toast.success("Device signed out");
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Could not revoke the session");
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      ariaHideApp={false}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.modalHeader}>
        <h3>{admin.userName}&rsquo;s devices</h3>
        <button type="button" className={styles.iconBtn} onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className={styles.modalBody}>
        {loading ? (
          <Spinner />
        ) : sessions.length === 0 ? (
          <p className={styles.empty}>Not signed in anywhere right now.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Device</th>
                <th>IP</th>
                <th>Signed in</th>
                <th>Last active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s._id}>
                  <td>
                    <Monitor size={14} /> {describeDevice(s.userAgent)}
                  </td>
                  <td className={styles.muted}>{s.ip || "—"}</td>
                  <td className={styles.muted}>
                    {new Date(s.createdAt).toLocaleString()}
                  </td>
                  <td className={styles.muted}>
                    {new Date(s.lastUsedAt).toLocaleString()}
                  </td>
                  <td>
                    <button
                      className={`${styles.iconBtn} ${styles.danger}`}
                      onClick={() => revokeOne(s._id)}
                      title="Sign out this device"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.modalFooter}>
        <button type="button" className={styles.secondaryBtn} onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className={styles.dangerBtn}
          onClick={onRevokedAll}
          disabled={sessions.length === 0}
        >
          Sign out of all devices
        </button>
      </div>
    </Modal>
  );
}

export default SessionsModal;
